import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Form, ToggleButton } from 'components';
import { FormWrapper } from 'components/Modal/styles';
import ModalForm from 'components/ModalForm/ModalForm';
import { useCreateEvent, useUpdateEvent } from 'api/events/hooks';
import validationSchemas from './schemas';

const EventForm = ({ type = 'create', initialValues, FormGroup }) => {
  const { t } = useTranslation(['pages', 'app']);

  const { mutate: createEvent, isPending: isCreatingEvent } = useCreateEvent();
  const { mutate: updateEvent, isPending: isUpdatingEvent } = useUpdateEvent();

  const formInitialValues = {
    uid: initialValues?.uid || '',
    name: initialValues?.name || '',
    description: initialValues?.description || '',
    date: initialValues?.date || '',
    is_shareable_on_retailer_page: initialValues?.is_shareable_on_retailer_page || false,
    is_auth_required_for_comments: initialValues?.is_auth_required_for_comments || false,
    is_auth_required_to_join: initialValues?.is_auth_required_to_join || false,
  };

  const handleSubmit = ({
    uid,
    name,
    description,
    date,
    is_shareable_on_retailer_page,
    is_auth_required_for_comments,
    is_auth_required_to_join,
  }) => {
    const eventData = {
      name,
      description,
      date,
      is_shareable_on_retailer_page: is_shareable_on_retailer_page ?? false,
      is_auth_required_for_comments: is_auth_required_for_comments ?? false,
      is_auth_required_to_join: is_auth_required_to_join ?? false,
    };

    if (type === 'update') {
      updateEvent({ uid, ...eventData });
    } else {
      createEvent(eventData);
    }
  };

  return (
    <ModalForm
      initialValues={formInitialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchemas.event}
      cancelLabel={t('app:cancel')}
      submitLabel={t(`app:${type}`)}
      isLoading={isCreatingEvent || isUpdatingEvent}
      ignoreClickedState
      isClosePopupOn={false}
    >
      {({ values, setFieldValue }) => (
        <FormWrapper type={type}>
          <FormGroup required label={t('app:name')}>
            <Form.Input type="text" name="name" />
          </FormGroup>
          <FormGroup label={t('app:description')}>
            <Form.Input type="text" name="description" />
          </FormGroup>
          <FormGroup required label={t('app:date')}>
            <Form.DateTimePicker name="date" />
          </FormGroup>
          <Form.GroupHorizontal
            label={t('app:shareLinkOnRetailerEventsTab')}
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <ToggleButton
              checked={values.is_shareable_on_retailer_page}
              onChange={(e) => setFieldValue('is_shareable_on_retailer_page', e.target.checked)}
            />
          </Form.GroupHorizontal>
          <Form.GroupHorizontal
            label={t('app:requireAuthForComments')}
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <ToggleButton
              checked={values.is_auth_required_for_comments}
              onChange={(e) => setFieldValue('is_auth_required_for_comments', e.target.checked)}
            />
          </Form.GroupHorizontal>
          <Form.GroupHorizontal
            label={t('app:requireAuthToJoin')}
            style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <ToggleButton
              checked={values.is_auth_required_to_join}
              onChange={(e) => setFieldValue('is_auth_required_to_join', e.target.checked)}
            />
          </Form.GroupHorizontal>
        </FormWrapper>
      )}
    </ModalForm>
  );
};

EventForm.propTypes = {
  type: PropTypes.oneOf(['create', 'update']),
  initialValues: PropTypes.object,
  FormGroup: PropTypes.func,
};

export default EventForm;
